import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  MerchantSettlementFilterKeys,
  MerchantSettlementListFilterOpts
} from "../../components/MerchantSettlementListPage/filter";
import {
  InitialMerchantSettlementFilterDataQuery_brands_edges_node,
  InitialMerchantSettlementFilterDataQuery_malls_edges_node
} from "../../types/InitialMerchantSettlementFilterDataQuery";
import {
  MerchantSettlementListUrlFilters,
  MerchantSettlementListUrlQueryParams,
  MerchantSettlementUrlFiltersEnum,
  MerchantSettlementUrlFiltersWithMultipleValues
} from "../../url";

export const MERCHANT_SETTLEMENT_FILTERS_KEY = "MerchantSettlementFilter";

export function getFilterOpts(
  params: MerchantSettlementListUrlFilters,
  brands: {
    initial: InitialMerchantSettlementFilterDataQuery_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialMerchantSettlementFilterDataQuery_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): MerchantSettlementListFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    payoutId: {
      active: !!maybe(() => params.payoutId),
      value: params.payoutId
    },
    settlementDate: {
      active: maybe(
        () =>
          [params.settlementDateFrom, params.settlementDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.settlementDateTo, ""),
        min: maybe(() => params.settlementDateFrom, "")
      }
    }
  };
}

export function getFilterVariables(params: MerchantSettlementListUrlFilters) {
  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    payoutId: params.payoutId ? btoa(params.payoutId) : null,

    settlementDate:
      params.settlementDateFrom && params.settlementDateTo
        ? getGteLteVariables({
            gte: params.settlementDateFrom
              ? moment(`${params.settlementDateFrom} 00:00:00`).format(
                  "YYYY-MM-DD HH:mm:ssZZ"
                )
              : null,

            lte: params.settlementDateTo
              ? moment(`${params.settlementDateTo} 00:00:00`)
                  .add(1, "days")
                  .format("YYYY-MM-DD HH:mm:ssZZ")
              : null
          })
        : {}
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<MerchantSettlementFilterKeys>,
  params: MerchantSettlementListUrlFilters
): MerchantSettlementListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case MerchantSettlementFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        MerchantSettlementUrlFiltersWithMultipleValues.brand
      );
    case MerchantSettlementFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        MerchantSettlementUrlFiltersWithMultipleValues.mall
      );
    case MerchantSettlementFilterKeys.settlementDate:
      return getMinMaxQueryParam(
        filter,
        MerchantSettlementUrlFiltersEnum.settlementDateFrom,
        MerchantSettlementUrlFiltersEnum.settlementDateTo
      );
    case MerchantSettlementFilterKeys.payoutId:
      return getSingleValueQueryParam(
        filter,
        MerchantSettlementUrlFiltersEnum.payoutId
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<MerchantSettlementListUrlFilters>(
  MERCHANT_SETTLEMENT_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  MerchantSettlementListUrlQueryParams,
  MerchantSettlementListUrlFilters
>({
  ...MerchantSettlementUrlFiltersEnum,
  ...MerchantSettlementUrlFiltersWithMultipleValues
});
